import {
  addDisplayName,
  addEffect,
  addHandlers,
  addProps,
  addRef,
  addState,
  addWrapper,
  flowMax,
} from 'ad-hok'
import { compact } from 'lodash/fp'
import React, { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import {
  BrandFeed,
  CategoryFeed,
  FeaturedUsersFeed,
  FeedItem,
  isBrandFeed,
  isFeedItemMedia,
  isLabelFeed,
  isPaginatedFeed,
  isTagFeed,
  isUserFeed,
  UserFeed,
} from 'shared/src/api/types'
import {
  addLocation,
  addNavigate,
  addOptionalIntPathParam,
  addOptionalPathParam,
} from 'shared/src/utils/addNavigate'
import { navBackOrHomepage } from 'shared/src/utils/navBack'
import { addNavigationState } from 'shared/src/utils/addNavigationState'
import { addAuthContext } from 'shared/src/utils/auth'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { typedAs } from 'shared/src/utils/typedAs'

import backButtonIcon from 'assets/caret-left.svg'
import { ImageButton } from 'components/ImageButton'
import { RodeoAndEditTabs } from 'components/RodeoAndEditTabs'
import { Footer } from 'components/footer'
import { NavBar } from 'components/navbar'
import { addLoadingAndErrorIndicators } from 'utils/addLoadingAndErrorIndicators'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { addShowRegisterContext } from 'utils/register'
import {
  RodeoOrEditViewType,
  addRodeoOrEditViewContextProvider,
} from 'utils/rodeoOrEditViewContext'
import { makeCreatorProfileUrl } from 'utils/routes'
import { FLUID_CONTAINER_STYLE } from 'utils/styles'
import { setPageTitle } from 'utils/title'
import { NAVBAR_Z_INDEX } from 'utils/zIndex'
import { ConstrainWidth } from '../../utils/layout'
import { Tabs } from '../menu/Tabs'
import { addFeedContextProvider } from './context'
import { Fluid } from './fluid'
import { TABS_HEIGHT, TABS_TOP } from './layout'
import { Banner } from 'components/banners/Banner'
import { GO2APP_URL } from 'utils/urls'

const useStyles = createUseStyles({
  container: {
    height: '100%',
    ...FLUID_CONTAINER_STYLE,
  },
  header: {
    position: 'relative',
  },
  roundupButton: {
    position: 'absolute',
    top: 16,
    right: 34,
    zIndex: NAVBAR_Z_INDEX,
  },
  tabs: {
    position: 'fixed',
    top: TABS_TOP,
    height: TABS_HEIGHT,
    paddingLeft: 40,
    zIndex: NAVBAR_Z_INDEX,
    minWidth: 320,
    maxWidth: 'calc(428px - 86px)',
    width: 'calc(100vw - 46px)',
  },
  navBarContainer: {
    position: 'relative',
  },
  backButton: {
    position: 'fixed',
    top: 0,
    color: 'white',
    zIndex: NAVBAR_Z_INDEX,
    marginTop: 15,
    marginLeft: 13,
  },
  loading: {
    position: 'relative',
    color: '#ffffff',
    fontSize: 30,
    top: '50%',
  },
  wantMore: {
    scrollSnapAlign: 'end',
  },
})

type InitialFeed = 'featured' | 'following'

interface Props {
  initialFeedId?: InitialFeed
}

export const Feed: FC<Props> = flowMax(
  addDisplayName('Feed'),
  addOptionalPathParam('username'),
  addOptionalIntPathParam('brandId'),
  addOptionalIntPathParam('tagId'),
  addOptionalIntPathParam('labelId'),
  addWrapper((render, { username, brandId, tagId, labelId, initialFeedId }) => (
    <React.Fragment
      key={`${username}-${brandId}-${tagId}-${labelId}-${initialFeedId}`}
    >
      {render()}
    </React.Fragment>
  )),
  addApi,
  addAuthContext,
  addState('page', 'setPage', 0),
  addState(
    'paginationSeed',
    'setPaginationSeed',
    Math.floor(Date.now() / 1000)
  ),
  addState(
    'feedData',
    'setFeedData',
    typedAs<UserFeed | CategoryFeed | BrandFeed | FeaturedUsersFeed>({
      media: [] as FeedItem[],
    })
  ),
  addDataLoadingFromPromise(
    'data',
    ({
      username,
      brandId,
      tagId,
      labelId,
      initialFeedId,
      api,
      isAuthenticated,
      page,
      paginationSeed,
    }) =>
      Promise.all([
        username
          ? api.users.feed(username)
          : brandId
          ? api.brands.feed(brandId)
          : tagId
          ? api.tags.feed(tagId)
          : labelId
          ? api.labels.feed(labelId)
          : initialFeedId === 'following'
          ? api.follows.feed()
          : initialFeedId === 'featured'
          ? api.users.featuredFeed()
          : api.feeds.rodeoNow(page, paginationSeed),
        isAuthenticated ? api.follows.creators() : Promise.resolve([]),
      ]),
    ['username', 'brandId', 'tagId', 'labelId', 'initialFeedId'],
    ({ username, brandId, tagId, labelId, initialFeedId }) => {
      const suffix = username
        ? `username-${username}`
        : brandId
        ? `brand-${brandId}`
        : tagId
        ? `tag-${tagId}`
        : labelId
        ? `label-${labelId}`
        : initialFeedId === 'following'
        ? 'following'
        : initialFeedId === 'featured'
        ? 'featured'
        : 'rodeo-now'
      return `feed-${suffix}`
    }
  ),
  addLoadingAndErrorIndicators('data', {
    redirectTo404IfError: true,
  }),
  addProps(({ data: [feed, follows] }) => ({
    feed,
    follows,
  })),
  addEffect(
    ({ feed, setFeedData }) =>
      () => {
        setFeedData(isPaginatedFeed(feed) ? feed.data : feed)
      },
    ['feed', 'setFeedData']
  ),
  addProps({
    rodeoOrEditView: typedAs<RodeoOrEditViewType>('rodeo'),
  }),
  addRodeoOrEditViewContextProvider,
  addTranslationHelpers,
  addProps(({ feed, initialFeedId, t }) => ({
    title: isUserFeed(feed)
      ? feed.user.username
      : isBrandFeed(feed)
      ? feed.brand.name
      : isTagFeed(feed)
      ? feed.tag.name
      : isLabelFeed(feed)
      ? feed.label.name
      : initialFeedId === 'following'
      ? t('feed.myCreatorsTitle')
      : '#rodeonow',
  })),
  setPageTitle(({ title }) => title ?? null),
  addNavigationState(
    'currentFeedId',
    'setCurrentFeedId',
    ({ initialFeedId, tagId }) => initialFeedId ?? tagId ?? 'rodeo-now'
  ),
  addNavigate,
  addProps(({ follows, t }) => ({
    extraTabs: compact([
      {
        displayName: '#rodeonow',
        path: '/',
        feedId: 'rodeo-now',
      },
      follows.length > 0
        ? {
            displayName: t('home.myCreatorsTab'),
            path: '/following',
            feedId: 'following',
          }
        : null,
    ]),
  })),
  addRef('scrollableContainerRef', typedAs<HTMLDivElement | null>(null)),
  addFeedContextProvider,
  addLocation,
  addNavigationState('showBackButton', 'setShowBackButton', false),
  addHandlers({
    onClickCurrentTab:
      ({ scrollableContainerRef }) =>
      () => {
        scrollableContainerRef.current?.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
      },
    onClickBack:
      ({ navigate, username, visibleMediaId, location, showBackButton }) =>
      () => {
        if (showBackButton) {
          navBackOrHomepage()
        } else if (username) {
          navigate(makeCreatorProfileUrl(username, 'posts'), {
            state: {
              ...location.state,
              scrollTo: visibleMediaId,
            },
            replace: true,
          })
        }
      },
  }),
  addShowRegisterContext,
  addTranslationHelpers,
  addProps(() => ({
    classes: useStyles(),
  })),
  addState('isMounted', 'setIsMounted', true),
  addState('paginationLoading', 'setPaginationLoading', false),
  addProps(({ paginationLoading, setPaginationLoading }) => ({
    paginationLoading,
    setPaginationLoading,
  })),
  addEffect(
    ({ setIsMounted }) =>
      () => {
        setIsMounted(true)
        return () => {
          setIsMounted(false)
        }
      },
    []
  ),
  addHandlers({
    requestNextPage:
      ({
        api,
        feed,
        page,
        setPage,
        paginationSeed,
        setFeedData,
        feedData,
        isMounted,
        setPaginationLoading,
      }) =>
      () => {
        if (
          isPaginatedFeed(feed) &&
          (feed.pages === null || page + 1 < feed.pages)
        ) {
          const nextPage = page + 1
          setPaginationLoading(true)
          api.feeds
            .rodeoNow(nextPage, paginationSeed)
            .then((newFeed) => {
              if (isMounted) {
                setPage(nextPage)
                setFeedData({
                  ...feedData,
                  media: [...feedData.media, ...newFeed.data.media],
                })
              }
            })
            .catch((error) => {
              if (isMounted) {
                console.error('Error fetching next page of feed:', error)
              }
            })
            .finally(() => {
              if (isMounted) setPaginationLoading(false)
            })
        }
      },
  }),
  ({
    feed,
    feedData,
    currentFeedId,
    extraTabs,
    onClickCurrentTab,
    onClickBack,
    scrollableContainerRef,
    showRegister,
    classes,
    t,
    requestNextPage,
    paginationLoading,
  }) => (
    <div
      ref={scrollableContainerRef}
      className={classes.container}
      style={
        paginationLoading
          ? {
              scrollSnapType: 'none',
            }
          : {}
      }
    >
      {!isUserFeed(feedData) &&
        !isBrandFeed(feedData) &&
        !isLabelFeed(feedData) && (
          <ConstrainWidth className={classes.header}>
            <Tabs
              data={{ tags: [] }}
              extraTabs={extraTabs}
              currentFeedId={currentFeedId}
              onClickCurrentTab={onClickCurrentTab}
            />
          </ConstrainWidth>
        )}
      {!isUserFeed(feedData) && (
        <ConstrainWidth className={classes.navBarContainer}>
          {isBrandFeed(feedData) ? (
            <NavBar title={feedData.brand.name} showGradient />
          ) : isLabelFeed(feedData) ? (
            <NavBar title={feedData.label.name} showGradient />
          ) : (
            <NavBar
              CenterComponent={RodeoAndEditTabs}
              showProfileButton
              showGradient
              showRoundupButton
            />
          )}

          {currentFeedId !== 'rodeo-now' && (
            <Banner
              banner={{
                unDismissable: true,
                noHider: true,
                accessibleMenu: true,
                textTranslationKey: 'banners.mobileOpenDownloadApp.appName',
                onCloseAnalyticsEvent: {
                  options: {
                    category: 'general',
                    action: 'banner_open_download_in_app_close',
                  },
                },
                link: {
                  title: t('banners.mobileOpenDownloadApp.exclusiveTitle'),
                  navigateTo: window.location.href.replace(
                    `${window.location.protocol}//${window.location.hostname}${
                      window.location.port ? ':' + window.location.port : ''
                    }`,
                    GO2APP_URL.replace(/\/$/, '')
                  ),
                  external: false,
                  onClickAnalyticsEvent: {
                    options: {
                      category: 'general',
                      action: 'banner_open_download_in_app_click',
                    },
                  },
                },
              }}
              onAction={() => {}}
            />
          )}
        </ConstrainWidth>
      )}
      {isUserFeed(feedData) && (
        <ConstrainWidth>
          <ImageButton
            image={backButtonIcon}
            width={20}
            height={20}
            onClick={onClickBack}
            className={classes.backButton}
          />
        </ConstrainWidth>
      )}
      <Fluid
        feed={feedData}
        scrollableContainerRef={scrollableContainerRef}
        requestNextPage={requestNextPage}
        mediaIds={
          isPaginatedFeed(feed)
            ? feedData.media.filter(isFeedItemMedia).map((item) => item.id)
            : []
        }
        paginationLoading={paginationLoading}
      />
      {isUserFeed(feedData) && !showRegister && (
        <ConstrainWidth>
          <Footer className={classes.wantMore} />
        </ConstrainWidth>
      )}
    </div>
  )
)
